import axios, { type AxiosRequestConfig } from "axios";
import { setupInterceptors, type InterceptorConfig } from "./lib/interceptors";
import {
  initializeApiClients,
  type ApiClientConfig,
  type ApiClientManager,
} from "./lib/api-clients";

const DEFAULT_BASE_URL = "https://api.oneapp.com/v1";

const DEFAULT_TIMEOUT = 30000;

export const AXIOS_INSTANCE = axios.create({
  baseURL: DEFAULT_BASE_URL,
  timeout: DEFAULT_TIMEOUT,
  headers: {
    "Content-Type": "application/json",
  },
});

let interceptorsConfigured = false;
let apiClientManager: ApiClientManager | null = null;

export function setupApiClient(config: InterceptorConfig = {}): void {
  if (interceptorsConfigured) {
    console.warn("[API] Interceptors already configured. Skipping setup.");
    return;
  }

  setupInterceptors(AXIOS_INSTANCE, config);
  interceptorsConfigured = true;

  if (config.enableLogging) {
    console.log("[API] Client configured with interceptors");
  }
}

export function setupMultiApiClient(config: ApiClientConfig): ApiClientManager {
  if (apiClientManager) {
    console.warn(
      "[API] Multi-client manager already initialized. Reinitializing...",
    );
  }

  apiClientManager = initializeApiClients(config);

  const defaultEndpoint =
    config.apiConfig.endpoints[config.apiConfig.defaultEndpoint];
  if (defaultEndpoint) {
    AXIOS_INSTANCE.defaults.baseURL = defaultEndpoint.baseURL;
    if (!interceptorsConfigured) {
      setupInterceptors(AXIOS_INSTANCE, {
        ...config,
        baseURL: defaultEndpoint.baseURL,
      });
      interceptorsConfigured = true;
    }
  }

  if (config.enableLogging) {
    console.log(
      "[API] Multi-client manager configured with endpoints:",
      Object.keys(config.apiConfig.endpoints).join(", "),
    );
  }

  return apiClientManager;
}

export function getApiClientManagerSafe(): ApiClientManager | null {
  return apiClientManager;
}

/**
 * Returns true if the multi-API client has already been initialized (e.g. by the host app).
 * Modules can use this to skip their own initializeApi and avoid calling restoreHostApi on unmount.
 */
export function isMultiApiClientInitialized(): boolean {
  return apiClientManager != null;
}

export function isApiClientConfigured(): boolean {
  return interceptorsConfigured;
}

export interface ClientSelectableRequestConfig extends AxiosRequestConfig {
  clientName?: string;
  skipRegionalBaseURL?: boolean;
}

export const customInstance = <T>(
  config: AxiosRequestConfig,
  options?: ClientSelectableRequestConfig,
): Promise<T> => {
  const controller = new AbortController();

  let instance: ReturnType<typeof axios.create>;

  if (apiClientManager) {
    const clientName = options?.clientName;

    if (clientName) {
      try {
        instance = apiClientManager.getClient(clientName);
      } catch {
        console.warn(
          `[API] Client "${clientName}" not found. Available clients: ${apiClientManager.getClientNames().join(", ")}. Using default client.`,
        );
        instance = apiClientManager.getDefaultClient();
      }
    } else {
      instance = apiClientManager.getDefaultClient();
    }
  } else {
    instance = AXIOS_INSTANCE;
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { clientName: _clientName, ...axiosOptions } = options || {};

  const promise = instance({
    ...config,
    ...axiosOptions,
    signal: controller.signal,
  }).then(({ data }) => data) as Promise<T> & { cancel: () => void };

  promise.cancel = () => {
    controller.abort();
  };

  return promise;
};

export type {
  InterceptorConfig,
  ApiError,
  CrossTabConfig,
  LoginPayload,
  AuthSyncCallbacks,
  HeaderRule,
  TokenStorage,
  RefreshTokenResponse,
} from "./lib/interceptors";

export type {
  CrossTabSync,
  CrossTabMessage,
} from "./lib/interceptors/cross-tab/types";
export {
  ApiErrorType,
  HttpStatusCode,
  createApiError,
  getErrorTypeFromStatus,
  broadcastLogout,
  broadcastLogin,
  subscribeToAuthSync,
  getCrossTabSync,
  CrossTabMessageType,
} from "./lib/interceptors";

export type { ApiConfig, ApiEndpoint } from "./lib/api-config";
export { createApiConfig, createHeaderRule } from "./lib/api-config";

export type { ApiClientConfig, ApiClientManager } from "./lib/api-clients";
export { getApiClient, getApiClientManager } from "./lib/api-clients";
